/**
 * Cluster plumbing.
 *
 * Caddy is the source of routing truth: from `$CLUSTER_DEFINITIONS` it strips the
 * cluster path prefix and injects a single `X-Cluster-Code` header. SSR resolves
 * the current cluster from that header and persists it into `useState('cluster')`,
 * which the client reads from the hydrated payload — no client-side resolution.
 *
 * `pathPrefix` is one name across three worlds: this wire JSON, the DB
 * `path_prefix` column, and the registry `pathPrefix` field. The env-derived
 * bootstrap MUST equal the DB value — a mismatch diverges the SSR base from the
 * post-hydration registry URLs (see the `??` seam in useCurrentCluster.ts).
 */

import type { LocationQuery } from 'vue-router';

import { stringifyQuery } from 'vue-router';
import { z } from 'zod';

import type { ServerLogErrorMessage } from '@/types/errorHandler';

const noop: ServerLogErrorMessage = () => {};

export interface ClusterDefinition {
  code: string;
  host: string;
  pathPrefix: string;
}

/** `au` → `/au`, `/au/` → `/au`, empty/absent → `''`. */
const normalisePath = (raw: unknown): string => {
  if (typeof raw !== 'string' || !raw) {
    return '';
  }
  const trimmed = raw.replace(/^\/+/, '').replace(/\/+$/, '');
  return trimmed ? `/${trimmed}` : '';
};

const clusterDefinitionSchema = z.object({
  code: z.string().min(1),
  host: z.string().min(1),
  pathPrefix: z.unknown().transform(normalisePath),
});

/** Mirrors the backend `ClusterProduct` enum (`App\Enums\Core\ClusterProduct`). */
export const CLUSTER_PRODUCTS = ['consumer', 'schools'] as const;
export type ClusterProduct = (typeof CLUSTER_PRODUCTS)[number];

/**
 * The full set of clusters Anyway serves, in public-facing form — the *global*
 * counterpart to the server-only routing `ClusterDefinition`. Set identically on
 * every deployment (each runs a separate DB and can't enumerate its siblings at
 * runtime). Drives the geo region switcher (`geo`/`name`/`host`) and cross-cluster
 * SEO hreflang (`code`/`hreflang`); exposed via `runtimeConfig.public.globalClusterDefinitions`.
 */
export interface GlobalClusterDefinition {
  /** Matched against the `X-Phoenix-Geo` header (`USA` | `AUS`). */
  geo: string;
  /** Matched against the current `X-Cluster-Code` for SEO. */
  code: string;
  name: string;
  /** BCP-47 hyphen-form language tag for hreflang (e.g. `en-AU`). */
  hreflang: string;
  host: string;
  pathPrefix: string;
  /**
   * The product surface this cluster serves — the geo-pairing key: region
   * switching and hreflang pair root AU ↔ root US and schools AU ↔ schools US,
   * never across products.
   */
  product: ClusterProduct;
}

const globalClusterDefinitionSchema = z.object({
  geo: z.string().trim().toUpperCase().pipe(z.string().min(1)),
  code: z.string().min(1),
  name: z.string().min(1),
  hreflang: z.string().min(1),
  host: z.string().min(1),
  pathPrefix: z.unknown().transform(normalisePath),
  // Absent means consumer (pre-product env values stay valid across the
  // staggered rollout); an unrecognised value drops the entry like any other
  // schema failure.
  product: z.enum(CLUSTER_PRODUCTS).default('consumer'),
});

/**
 * Parse a `*_CLUSTER_DEFINITIONS` runtime value. Accepts a JSON string (the
 * build-time runtimeConfig default) or an already-parsed array (a deployed
 * `NUXT_*` override arrives via destr). Unset is legitimate (`[]`); a
 * configured-but-not-an-array value throws so the caller can surface the
 * misconfig; individual entries failing the schema are dropped.
 */
const parseDefinitions = <T>(raw: unknown, schema: z.ZodType<T>, label: string): T[] => {
  if (!raw) {
    return [];
  }

  const parsed: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
  if (!Array.isArray(parsed)) {
    throw new TypeError(`${label} must be a JSON array`);
  }

  return parsed.flatMap((entry) => {
    const result = schema.safeParse(entry);
    return result.success ? [result.data] : [];
  });
};

export const parseClusterDefinitions = (raw: unknown): ClusterDefinition[] =>
  parseDefinitions(raw, clusterDefinitionSchema, 'CLUSTER_DEFINITIONS');

export const parseGlobalClusterDefinitions = (raw: unknown): GlobalClusterDefinition[] =>
  parseDefinitions(raw, globalClusterDefinitionSchema, 'GLOBAL_CLUSTER_DEFINITIONS');

/** Absolute URL for a cluster/region: `<scheme>://<host><pathPrefix>`. */
export const buildClusterUrl = (
  host: string,
  pathPrefix: string,
  opts: { scheme: 'http' | 'https' },
): string => `${opts.scheme}://${host}${pathPrefix}`;

export interface ResolvedCurrentCluster {
  code: string | null;
  pathPrefix: string;
}

export const EMPTY_CLUSTER: ResolvedCurrentCluster = { code: null, pathPrefix: '' };

/**
 * Resolve the current cluster from an `X-Cluster-Code` header value. Returns
 * `EMPTY_CLUSTER` (root cluster) when no header is set or the code is unknown.
 * Server-only — the client reads the already-resolved value from shared state.
 * An unknown code is a Caddy/Laravel misconfig — the caller passes a Datadog
 * logger (`logErrorMessage`); defaults to a no-op for callers without one.
 */
export const resolveClusterFromHeader = (
  definitions: ClusterDefinition[],
  clusterCodeHeader: string | null | undefined,
  logErrorMessage: ServerLogErrorMessage = noop,
): ResolvedCurrentCluster => {
  if (!clusterCodeHeader) {
    return EMPTY_CLUSTER;
  }
  const def = definitions.find(d => d.code === clusterCodeHeader);
  if (!def) {
    // A non-empty code matching no definition. Degrade to root, but log at error
    // level so the silent "AU routed as US" case is alertable.
    logErrorMessage('[resolveClusterFromHeader] unknown cluster code', { cluster_code: clusterCodeHeader, fallback: 'root' });
    return EMPTY_CLUSTER;
  }
  return { code: def.code, pathPrefix: def.pathPrefix };
};

/**
 * Parse `CLUSTER_DEFINITIONS` + resolve the current cluster's path prefix from an
 * `X-Cluster-Code` header, swallowing a malformed-definitions misconfig (logged,
 * degrade to root). Single source for the parse + resolve + try/catch that server
 * entry points (the referral redirect, the sitemap) would re-spell. Forwards
 * `logErrorMessage` so the unknown-code case is captured too. Returns the bare
 * prefix (`''` for root); callers needing a routable base use `prefix || '/'`.
 */
export const resolveClusterPrefixFromHeader = (
  rawDefinitions: unknown,
  clusterCodeHeader: string | null | undefined,
  context: string,
  logErrorMessage: ServerLogErrorMessage = noop,
): string => {
  try {
    const definitions = parseClusterDefinitions(rawDefinitions);
    return resolveClusterFromHeader(definitions, clusterCodeHeader, logErrorMessage).pathPrefix;
  }
  catch (err) {
    logErrorMessage(`[${context}] invalid CLUSTER_DEFINITIONS`, { error: err });
    return '';
  }
};

/**
 * Parse `GLOBAL_CLUSTER_DEFINITIONS` + resolve the current cluster's product
 * from an `X-Cluster-Code` header — the server-side counterpart of
 * `filterClustersByProduct`'s product lookup, for nitro handlers that have no
 * cluster registry (the robots/sitemap middleware). Defaults to the consumer
 * surface on an unknown code or a malformed-definitions misconfig (logged),
 * preserving pre-product behaviour.
 */
export const resolveClusterProductFromHeader = (
  rawGlobalDefinitions: unknown,
  clusterCodeHeader: string | null | undefined,
  context: string,
  logErrorMessage: ServerLogErrorMessage = noop,
): ClusterProduct => {
  try {
    const definitions = parseGlobalClusterDefinitions(rawGlobalDefinitions);
    return definitions.find(d => d.code === clusterCodeHeader)?.product ?? 'consumer';
  }
  catch (err) {
    logErrorMessage(`[${context}] invalid GLOBAL_CLUSTER_DEFINITIONS`, { error: err });
    return 'consumer';
  }
};

/**
 * Parse `GLOBAL_CLUSTER_DEFINITIONS` + resolve the current cluster's geo from an
 * `X-Cluster-Code` header — the sibling of `resolveClusterProductFromHeader` for
 * nitro handlers that key region-by-region rollout (the sitemap's bootstrap flag
 * read) off the geo. Returns `undefined` on an unknown code or a
 * malformed-definitions misconfig (logged), so the caller falls back to whatever
 * it does without a region.
 */
export const resolveClusterGeoFromHeader = (
  rawGlobalDefinitions: unknown,
  clusterCodeHeader: string | null | undefined,
  context: string,
  logErrorMessage: ServerLogErrorMessage = noop,
): string | undefined => {
  try {
    const definitions = parseGlobalClusterDefinitions(rawGlobalDefinitions);
    return definitions.find(d => d.code === clusterCodeHeader)?.geo;
  }
  catch (err) {
    logErrorMessage(`[${context}] invalid GLOBAL_CLUSTER_DEFINITIONS`, { error: err });
    return undefined;
  }
};

/**
 * Join a cluster path prefix to an app path, collapsing the root-path edge: a
 * prefixed cluster's `/` maps to the bare prefix (no trailing slash); the root
 * cluster (empty prefix) keeps the path verbatim.
 */
export const joinClusterPath = (prefix: string, path: string): string =>
  prefix === '' ? path : `${prefix}${path === '/' ? '' : path}`;

/**
 * Inverse of `joinClusterPath`: strip a cluster path prefix, mapping the bare
 * prefix back to `/` and leaving unprefixed (or foreign-prefixed) paths
 * verbatim.
 */
export const splitClusterPath = (prefix: string, path: string): string => {
  if (!prefix || (path !== prefix && !path.startsWith(`${prefix}/`))) {
    return path;
  }
  return path.slice(prefix.length) || '/';
};

// Query params that must never ride a region switch. A blacklist of explicit
// names, not prefixes or an allowlist, by design — unknown params (utm_*,
// referral codes, …) pass through; only the keys named here drop.
const NON_TRANSFERABLE_QUERY_PARAMS = new Set([
  // Auth/verification secrets and PII (OAuth code/state, onboarding email/OTP).
  'code',
  'state',
  'email',
  'otp',
  'token',
  // Student-role invite capability token: `checkStudentRoleInvite` resolves it
  // to the invitee's email and school without authentication.
  'invitecode',
  // Social-login callback prefill/PII (parseSocialReturnFromQuery).
  'social_flow',
  'social_provider',
  'social_email',
  'social_first_name',
  'social_last_name',
  'social_auth_failed',
  'social_scene_score',
  // The /login card's hand-off of a verified new email to /onboarding (PII).
  'signup_email',
  // Deployment-specific signup payloads: the login page's selectedEntity /
  // entryTracking blobs embed region-DB entity IDs that are meaningless (or
  // wrong) on the sibling deployment.
  'selectedentity',
  'entrytracking',
]);

/**
 * Strip non-transferable query params (secrets, PII, deployment-specific
 * payloads) before they reach a region-switch URL or an analytics payload (the
 * `region_switched` event ships the query to third-party providers).
 */
export const omitNonTransferableQueryParams = (query: LocationQuery): LocationQuery =>
  Object.fromEntries(
    Object.entries(query).filter(([key]) => {
      const normalised = key.toLowerCase();
      return !NON_TRANSFERABLE_QUERY_PARAMS.has(normalised);
    }),
  );

/**
 * Whether the query carries any non-transferable param — the signal of an
 * in-flight auth/verification flow (OAuth `code`/`state`, OTP prefill,
 * onboarding payloads). A region switch would have to strip these, silently
 * breaking the flow, so an automatic redirect must stand down when present.
 */
export const hasNonTransferableQueryParams = (query: LocationQuery): boolean =>
  Object.keys(query).some(key => NON_TRANSFERABLE_QUERY_PARAMS.has(key.toLowerCase()));

/**
 * Absolute URL for a region switch, assembled from a prefix-free path and query.
 * The caller decides whether the current path carries over (it must exist in the
 * destination region) or falls back to the home path. Hash fragments are not
 * carried: they never reach the server, so an SSR-rendered URL couldn't include
 * them without a hydration mismatch.
 */
export const buildRegionSwitchUrl = (
  host: string,
  pathPrefix: string,
  opts: { scheme: 'http' | 'https'; path: string; query: LocationQuery },
): string => {
  const queryString = stringifyQuery(opts.query);
  return `${opts.scheme}://${host}${joinClusterPath(pathPrefix, opts.path)}${queryString ? `?${queryString}` : ''}`;
};

/**
 * The subset of `clusters` sharing the current cluster's product — the geo
 * pairing rule: a consumer cluster only ever pairs with the sibling consumer
 * cluster, schools with schools. The current product comes from the definition
 * matching `currentCode`; a null or unlisted code (the test cluster, staging
 * codes) defaults to the consumer surface, preserving pre-product behaviour.
 */
export const filterClustersByProduct = (
  clusters: GlobalClusterDefinition[],
  currentCode: string | null,
): GlobalClusterDefinition[] => {
  const currentProduct: ClusterProduct
    = clusters.find(cluster => cluster.code === currentCode)?.product ?? 'consumer';
  return clusters.filter(cluster => cluster.product === currentProduct);
};

/**
 * Cross-cluster hreflang `<link>` alternates for a served path. Callers pass a
 * same-product cluster list (`useGlobalClusters` pre-filters via
 * `filterClustersByProduct`) — alternates never cross the consumer/schools
 * boundary. All listed clusters share the apex host, so every alternate
 * (and `x-default`) is built
 * from the current request `apexOrigin` + the cluster's `pathPrefix`. Returns
 * `[]` when the current cluster isn't listed, so a deployment never asserts
 * alternates it can't vouch for. `localPath` is prefix-free (Caddy strips it).
 * The inline return shape (not a named interface) stays assignable to
 * nuxt-seo-utils' `ResolvableLink`, which needs an index signature.
 */
export const buildHreflangLinks = (
  clusters: GlobalClusterDefinition[],
  currentCode: string | null,
  apexOrigin: string,
  localPath: string,
): { rel: 'alternate'; hreflang: string; href: string }[] => {
  if (!currentCode || !clusters.some(cluster => cluster.code === currentCode)) {
    return [];
  }

  const links = clusters.map(cluster => ({
    rel: 'alternate' as const,
    hreflang: cluster.hreflang,
    href: joinClusterPath(`${apexOrigin}${cluster.pathPrefix}`, localPath),
  }));

  links.push({ rel: 'alternate' as const, hreflang: 'x-default', href: joinClusterPath(apexOrigin, localPath) });

  return links;
};

/**
 * Cluster-scoped API URL: `<scheme>://<host>[:port]<pathPrefix>/api`. Always
 * strips a leading `www.`. `withPort` replaces any port with the scheme's
 * canonical one for server-side fetches that target the app directly; without
 * it (browser) a non-standard port is preserved so the API stays same-origin
 * (e.g. the mobile testing proxy on :8443).
 */
export const buildApiUrl = (
  host: string,
  pathPrefix: string,
  opts: { useHttp: boolean; withPort: boolean },
): string => {
  const normalisedHost = host.replace(/^www\./, '').replace(/:\d+$/, '');
  const scheme = opts.useHttp ? 'http' : 'https';
  const apiHost = opts.withPort
    ? `${normalisedHost}:${opts.useHttp ? '80' : '443'}`
    : host.replace(/^www\./, '');
  return `${scheme}://${apiHost}${pathPrefix}/api`;
};
